import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { createBrowserHistory } from 'history';
import type { IHistoryContext } from './types';
import { useViewTransitionRunner } from './useViewTransitionRunner';

const history = createBrowserHistory();

const HistoryContext = createContext<IHistoryContext>({
  location: history.location.pathname,
  action: history.action,
  searchParams: new URLSearchParams(history.location.search),
});

export const useLocation = () => {
  const context = useContext(HistoryContext);
  if (!context) {
    throw new Error('useLocation must be used within HistoryNexus');
  }
  return context.location;
};

export const useSearchParams = () => {
  const context = useContext(HistoryContext);
  if (!context) {
    throw new Error('useSearchParams must be used within HistoryNexus');
  }
  return context.searchParams;
};

export const HistoryNexus = ({ children }: { children: React.ReactNode }) => {
  const [historyLocation, setHistoryLocation] = useState(history.location);
  const [action, setAction] = useState(history.action);
  const runViewTransitions = useViewTransitionRunner(historyLocation.pathname);

  useEffect(() => {
    const unlisten = history.listen((update) => {
      const { location, action } = update;
      runViewTransitions({
        nextPathname: location.pathname,
        navigate: () => {
          setHistoryLocation(location);
          setAction(action);
        },
      });
    });
    return () => {
      unlisten();
    };
  }, []);

  const value = useMemo(
    () => ({
      action,
      location: historyLocation.pathname,
      searchParams: new URLSearchParams(historyLocation.search),
    }),
    [historyLocation, action]
  );

  return <HistoryContext.Provider value={value}>{children}</HistoryContext.Provider>;
};

/**
 * Navigate to a new page. Can be called from anywhere in the app.
 * @param href - The href to navigate to.
 * @param options?.replace - If true, the navigation will replace the current history entry.
 */
export const navigate = (href: string, options?: { replace?: boolean }) => {
  const navigation = options?.replace ? history.replace : history.push;
  navigation(href);
};

/**
 * Redirect to a new page. Can be called from anywhere in the app.
 * @param href - The href to redirect to.
 */
export const redirect = (href: string) => {
  history.replace(href);
};
